import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const CONFIG = {
  pdf: {
    accept: { 'application/pdf': ['.pdf'] },
    ext: '.pdf',
    endpoint: '/api/convert/split-pdf',
    filename: 'split_pdf.zip',
    accentColor: '#ef4444',
    icon: '📄',
    modes: [
      {
        key: 'individual',
        label:  { en: 'Individual Pages',    ar: 'صفحات منفردة',         fa: 'صفحات جداگانه' },
        desc:   { en: 'One PDF per page',    ar: 'ملف PDF لكل صفحة',     fa: 'یک PDF برای هر صفحه' },
        extra: null,
      },
      {
        key: 'ranges',
        label:  { en: 'Custom Ranges',       ar: 'نطاقات مخصصة',         fa: 'محدوده دلخواه' },
        desc:   { en: 'e.g. 1-3, 5, 7-10',  ar: 'مثال: 1-3، 5، 7-10',  fa: 'مثال: 1-3، 5، 7-10' },
        extra: 'ranges',
      },
      {
        key: 'chunks',
        label:  { en: 'Equal Chunks',        ar: 'أجزاء متساوية',        fa: 'قطعات مساوی' },
        desc:   { en: 'N pages per part',    ar: 'N صفحة لكل جزء',      fa: 'N صفحه در هر بخش' },
        extra: 'chunk',
      },
    ],
  },
  word: {
    accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    ext: '.docx',
    endpoint: '/api/convert/split-word',
    filename: 'split_word.zip',
    accentColor: '#2563eb',
    icon: '📝',
    modes: [
      {
        key: 'page_break',
        label:  { en: 'By Page Break',       ar: 'عند فواصل الصفحات',    fa: 'بر اساس شکست صفحه' },
        desc:   { en: 'Split at Word page break markers',
                  ar: 'تقسيم عند علامات كسر الصفحة',
                  fa: 'تقسیم در نقاط شکست صفحه Word' },
        extra: null,
      },
      {
        key: 'heading1',
        label:  { en: 'By Heading 1',        ar: 'عند العنوان 1',         fa: 'بر اساس عنوان ۱' },
        desc:   { en: 'Each Heading 1 starts a new document',
                  ar: 'كل عنوان 1 يبدأ مستنداً جديداً',
                  fa: 'هر عنوان ۱ سند جدیدی را شروع می‌کند' },
        extra: null,
      },
    ],
  },
  pptx: {
    accept: {
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
    },
    ext: '.pptx / .ppt',
    endpoint: '/api/convert/split-pptx',
    filename: 'split_pptx.zip',
    accentColor: '#f97316',
    icon: '📊',
    modes: [
      {
        key: 'individual',
        label:  { en: 'Individual Slides',   ar: 'شرائح منفردة',         fa: 'اسلایدهای جداگانه' },
        desc:   { en: 'One PPTX per slide',  ar: 'ملف PPTX لكل شريحة',  fa: 'یک PPTX برای هر اسلاید' },
        extra: null,
      },
      {
        key: 'ranges',
        label:  { en: 'Custom Ranges',       ar: 'نطاقات مخصصة',         fa: 'محدوده دلخواه' },
        desc:   { en: 'e.g. 1-3, 5, 7-10',  ar: 'مثال: 1-3، 5، 7-10',  fa: 'مثال: 1-3، 5، 7-10' },
        extra: 'ranges',
      },
      {
        key: 'chunks',
        label:  { en: 'Equal Chunks',        ar: 'أجزاء متساوية',        fa: 'قطعات مساوی' },
        desc:   { en: 'N slides per part',   ar: 'N شريحة لكل جزء',     fa: 'N اسلاید در هر بخش' },
        extra: 'chunk',
      },
    ],
  },
};

const T = {
  drop:     { en: 'Drag file here or click to select',  ar: 'اسحب الملف هنا أو انقر للاختيار',   fa: 'فایل را اینجا بکشید یا کلیک کنید' },
  split:    { en: '✂ Split Now',                        ar: '✂ تقسيم الآن',                        fa: '✂ تقسیم کن' },
  splitting:{ en: 'Splitting…',                         ar: 'جاري التقسيم…',                       fa: 'در حال تقسیم...' },
  success:  { en: 'Split successfully!',                ar: 'تم التقسيم بنجاح!',                   fa: 'تقسیم موفقیت‌آمیز بود!' },
  modeLabel:{ en: 'Split Mode',                         ar: 'نمط التقسيم',                         fa: 'حالت تقسیم' },
  rangeLabel:{ en: 'Page/Slide Ranges',                 ar: 'نطاقات الصفحات / الشرائح',            fa: 'محدوده صفحات / اسلایدها' },
  rangePh:  { en: 'e.g. 1-3, 5, 7-10',                 ar: 'مثال: 1-3، 5، 7-10',                 fa: 'مثال: 1-3، 5، 7-10' },
  chunkLabel:{ en: 'Pages / Slides per Part',           ar: 'الصفحات / الشرائح لكل جزء',          fa: 'صفحات / اسلاید در هر بخش' },
  noFile:   { en: 'Please select a file first',         ar: 'يرجى تحديد ملف أولاً',                fa: 'لطفاً ابتدا یک فایل انتخاب کنید' },
};

const t = (key, lang) => T[key]?.[lang] || T[key]?.en || key;

export default function SplitCard({ lang, fileType }) {
  const cfg = CONFIG[fileType];
  const { token } = useAuth();
  const [file, setFile]         = useState(null);
  const [mode, setMode]         = useState(fileType === 'pdf' ? 'chunks' : cfg.modes[0].key);
  const [rangesStr, setRangesStr] = useState('');
  const [chunkSize, setChunkSize] = useState(10);
  const [loading, setLoading]   = useState(false);
  const [progress, setProgress] = useState(0);

  const rtl = lang === 'ar' || lang === 'fa';

  const allowedExts = Object.values(cfg.accept).flat();

  // Accept every file — validate by extension in handleSplit so nothing is silently rejected
  const onDrop = useCallback((accepted, rejected) => {
    const f = accepted[0] ?? rejected?.[0]?.file;
    if (f) setFile(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    // no accept filter — prevents silent MIME-type rejection on Windows
  });

  const currentMode = cfg.modes.find(m => m.key === mode);

  const handleSplit = async () => {
    if (!file) {
      toast.error(t('noFile', lang));
      return;
    }
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExts.includes(ext)) {
      toast.error(`Wrong file type. Expected: ${allowedExts.join(', ')}`);
      return;
    }
    setLoading(true);
    setProgress(0);
    const safeExt = file.name.split('.').pop().toLowerCase();
    const safeName = `file.${safeExt}`;
    const fd = new FormData();
    fd.append('file', file, safeName);
    fd.append('mode', mode);
    if (currentMode?.extra === 'ranges') fd.append('ranges_str', rangesStr);
    if (currentMode?.extra === 'chunk')  fd.append('chunk_size', String(chunkSize));
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await axios.post(`${API}${cfg.endpoint}`, fd, {
        responseType: 'blob',
        headers,
        timeout: 600000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 50));
        },
        onDownloadProgress: (e) => {
          if (e.total) setProgress(50 + Math.round((e.loaded / e.total) * 48));
          else setProgress(90);
        },
      });
      setProgress(99);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = cfg.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
      toast.success(t('success', lang), {
        style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' },
      });
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل التقسيم' : lang === 'fa' ? 'تقسیم ناموفق بود' : 'Split failed';
      if (err.response?.data instanceof Blob) {
        try { const txt = await err.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch {}
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <div>
      {/* Mode picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
          textAlign: rtl ? 'right' : 'left',
        }}>
          {t('modeLabel', lang)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {cfg.modes.map(m => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              style={{
                background: mode === m.key ? `${cfg.accentColor}22` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${mode === m.key ? cfg.accentColor : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                padding: '10px 14px',
                textAlign: rtl ? 'right' : 'left',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                fontSize: '13px', fontWeight: 600,
                color: mode === m.key ? '#fff' : 'rgba(255,255,255,0.75)',
              }}>
                {m.label[lang] || m.label.en}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                {m.desc[lang] || m.desc.en}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Range input */}
      {currentMode?.extra === 'ranges' && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
            textAlign: rtl ? 'right' : 'left',
          }}>
            {t('rangeLabel', lang)}
          </div>
          <input
            type="text"
            value={rangesStr}
            onChange={e => setRangesStr(e.target.value)}
            placeholder={t('rangePh', lang)}
            dir="ltr"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px', padding: '10px 12px',
              color: '#fff', fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Chunk size input */}
      {currentMode?.extra === 'chunk' && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
            textAlign: rtl ? 'right' : 'left',
          }}>
            {t('chunkLabel', lang)}
          </div>
          <input
            type="number"
            min={1} max={500}
            value={chunkSize}
            onChange={e => setChunkSize(Math.max(1, parseInt(e.target.value) || 1))}
            dir="ltr"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px', padding: '10px 12px',
              color: '#fff', fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? cfg.accentColor : 'rgba(255,255,255,0.15)'}`,
          borderRadius: '12px',
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? `${cfg.accentColor}10` : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>{cfg.icon}</div>
        {file ? (
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: 600 }}>
            {file.name}
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '4px', fontWeight: 400 }}>
              {(file.size / 1024).toFixed(0)} KB
            </div>
          </div>
        ) : (
          <>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{t('drop', lang)}</div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>
              {cfg.ext}
            </div>
          </>
        )}
      </div>

      {file && (
        <button
          onClick={() => setFile(null)}
          style={{
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.3)', fontSize: '12px',
            cursor: 'pointer', padding: '0 0 12px 0', display: 'block',
          }}
        >
          {lang === 'ar' ? 'إزالة الملف' : lang === 'fa' ? 'حذف فایل' : 'Remove file'}
        </button>
      )}

      <button
        className="btn-primary"
        onClick={handleSplit}
        disabled={loading}
        style={{ opacity: loading ? 0.7 : 1 }}
      >
        {loading ? <><span className="spinner" />{t('splitting', lang)} {progress > 0 ? `${progress}%` : ''}</> : t('split', lang)}
      </button>

      {loading && progress > 0 && (
        <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', overflow: 'hidden', height: '4px' }}>
          <div style={{
            height: '100%', borderRadius: '6px',
            background: cfg.accentColor,
            width: `${progress}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>
      )}
    </div>
  );
}
